if (!customElements.get('variant-table-form')) {
  const stockStatusMonthsShort = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const stockStatusWeekdaysLong = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

  const addBusinessDays = (startDate, businessDays = 0) => {
    if (typeof window.addBusinessDays === 'function') {
      return window.addBusinessDays(startDate, businessDays);
    }

    const date = new Date(startDate);
    let remainingDays = Number(businessDays) || 0;

    while (remainingDays > 0) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) remainingDays -= 1;
    }

    return date;
  };

  const getStockStatusDate = (daysToAdd = 0) => {
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    return addBusinessDays(date, daysToAdd);
  };

  const formatStockStatusDeliveryRange = (startDays) => {
    if (!Number.isFinite(startDays) || startDays <= 0) return '';
    const startDate = getStockStatusDate(startDays);
    const endDate = getStockStatusDate(startDays + 14);
    return `Recíbelo el ${startDate.getDate()} ${stockStatusMonthsShort[startDate.getMonth()]} - ${endDate.getDate()} ${stockStatusMonthsShort[endDate.getMonth()]}`;
  };

  const formatStockStatusImmediateDate = (daysToAdd = 2) => {
    const date = getStockStatusDate(daysToAdd);
    return `Recíbelo el ${stockStatusWeekdaysLong[date.getDay()]}, ${date.getDate()} ${stockStatusMonthsShort[date.getMonth()]}`;
  };

  customElements.define('variant-table-form', class VariantTableForm extends HTMLElement {
    constructor() {
      super();
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.errorMessageWrapper = this.querySelector('.variant-table__error-message-wrapper');
      this.errorMessage = this.querySelector('.variant-table__error-message');
      this.bulkButton = this.querySelector('[data-add-selected]');
      this.isSubmitting = false;
    }

    connectedCallback() {
      this.rows = [...this.querySelectorAll('.variant-table__row')];

      this.rows.forEach((row) => {
        const input = row.querySelector('.variant-table__quantity-input');
        const rowButton = row.querySelector('[data-add-row]');

        row.querySelectorAll('[data-quantity-action]').forEach((button) => {
          button.addEventListener('click', () => this.handleQuantityButton(row, button.dataset.quantityAction));
        });

        input?.addEventListener('input', () => {
          this.updateButtonsState();
          this.updateStockMessage(row);
        });
        input?.addEventListener('blur', () => {
          this.normalizeInput(row);
          this.updateButtonsState();
          this.updateStockMessage(row);
        });

        rowButton?.addEventListener('click', () => this.submitRows([row]));
      });

      this.bulkButton?.addEventListener('click', () => {
        const rows = this.getRowsWithQuantity();
        this.submitRows(rows);
      });

      this.rows.forEach((row) => this.updateStockMessage(row));
      this.updateButtonsState();
    }

    getRowQuantityConfig(row) {
      return {
        min: Number(row.dataset.min || 1),
        step: Number(row.dataset.step || 1),
        max: row.dataset.max ? Number(row.dataset.max) : null,
      };
    }

    normalizeQuantityValue(row, rawValue) {
      const { min, step, max } = this.getRowQuantityConfig(row);
      let value = Number(rawValue);

      if (!Number.isFinite(value) || value <= 0) return 0;
      if (value < min) value = min;

      const remainder = (value - min) % step;
      if (remainder !== 0) value += step - remainder;

      if (max !== null) value = Math.min(value, max);
      return value;
    }

    normalizeInput(row) {
      const input = row.querySelector('.variant-table__quantity-input');
      if (!input) return 0;
      const normalizedValue = this.normalizeQuantityValue(row, input.value);
      input.value = normalizedValue;
      return normalizedValue;
    }

    handleQuantityButton(row, action) {
      const input = row.querySelector('.variant-table__quantity-input');
      if (!input || input.disabled) return;

      const { min, step, max } = this.getRowQuantityConfig(row);
      let value = Number(input.value);
      if (!Number.isFinite(value) || value < 0) value = 0;

      if (action === 'increase') {
        value = value <= 0 ? min : value + step;
        if (max !== null) value = Math.min(value, max);
      } else {
        if (value <= min) {
          value = 0;
        } else {
          value = Math.max(min, value - step);
        }
      }

      input.value = value;
      this.updateButtonsState();
    }

    getRowsWithQuantity() {
      return this.rows.filter((row) => {
        if (row.dataset.variantAvailable !== 'true') return false;
        return this.normalizeInput(row) > 0;
      });
    }

    updateButtonsState() {
      this.rows.forEach((row) => {
        const rowButton = row.querySelector('[data-add-row]');
        const isAvailable = row.dataset.variantAvailable === 'true';
        const quantity = this.normalizeQuantityValue(row, row.querySelector('.variant-table__quantity-input')?.value);
        this.updateStockMessage(row, quantity);
        if (rowButton) rowButton.disabled = !isAvailable || quantity <= 0 || this.isSubmitting;
      });

      if (this.bulkButton) {
        this.bulkButton.disabled = this.isSubmitting || this.getRowsWithQuantity().length === 0;
      }
    }

    updateStockMessage(row, quantity = null) {
      const stockContainer = row.querySelector('[data-stock-message]');
      const stockPrimary = stockContainer?.querySelector('[data-stock-primary]');
      const stockSecondary = stockContainer?.querySelector('[data-stock-secondary]');
      const inStockIcon = stockContainer?.querySelector('[data-stock-icon="in-stock"]');
      const deliveryIcon = stockContainer?.querySelector('[data-stock-icon="delivery"]');

      if (!stockContainer || !stockPrimary || !stockSecondary) return;

      const selectedQuantity = quantity ?? this.normalizeQuantityValue(row, row.querySelector('.variant-table__quantity-input')?.value);
      const immediateStock = Number(row.dataset.immediateStock || 0);
      const noStockDeliveryDays = Number(row.dataset.noStockDeliveryDays || 0);
      const hasDeliveryInfo = row.dataset.hasDeliveryInfo === 'true';
      const inStockDeliveryDays = Number(row.dataset.inStockDeliveryDays || 2);
      const stockTracked = row.dataset.stockTracked === 'true';
      const isAvailable = row.dataset.variantAvailable === 'true';
      const setState = (state) => {
        stockContainer.classList.remove(
          'variant-table__stock--in-stock',
          'variant-table__stock--partial',
          'variant-table__stock--out-of-stock'
        );
        stockContainer.classList.add(`variant-table__stock--${state}`);
        inStockIcon?.classList.toggle('hidden', state !== 'in-stock');
        deliveryIcon?.classList.toggle('hidden', state === 'in-stock');
      };
      const setCopy = (primaryText, secondaryText = '') => {
        stockPrimary.textContent = primaryText;
        stockSecondary.textContent = secondaryText;
        stockSecondary.classList.toggle('hidden', !secondaryText);
      };
      const setVisibility = (visible) => {
        stockContainer.classList.toggle('hidden', !visible);
      };

      if (!hasDeliveryInfo) {
        setVisibility(false);
        setCopy('', '');
        return;
      }

      setVisibility(true);

      if (!stockTracked && isAvailable) {
        setState('in-stock');
        setCopy('En stock. Envío inmediato.', formatStockStatusImmediateDate(inStockDeliveryDays));
        return;
      }

      if (immediateStock <= 0) {
        setState('out-of-stock');
        setCopy(formatStockStatusDeliveryRange(noStockDeliveryDays));
        return;
      }

      if (selectedQuantity > immediateStock) {
        setState('partial');
        setCopy(formatStockStatusDeliveryRange(noStockDeliveryDays));
        return;
      }

      setState('in-stock');
      setCopy('En stock. Envío inmediato.', formatStockStatusImmediateDate(inStockDeliveryDays));
    }

    setLoadingState(loading) {
      this.isSubmitting = loading;

      if (this.bulkButton) {
        this.bulkButton.classList.toggle('loading', loading);
        this.bulkButton.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !loading);
      }

      this.querySelectorAll('.variant-table__quantity-input, .variant-table__quantity-button, [data-add-row], [data-add-selected]').forEach((element) => {
        if (loading) {
          element.setAttribute('disabled', 'disabled');
        } else if (element.dataset.addRow !== undefined || element.dataset.addSelected !== undefined) {
          element.removeAttribute('disabled');
        } else if (element.closest('.variant-table__row')?.dataset.variantAvailable === 'true') {
          element.removeAttribute('disabled');
        }
      });

      this.updateButtonsState();
    }

    handleErrorMessage(message = '') {
      if (!this.errorMessageWrapper || !this.errorMessage) return;
      this.errorMessageWrapper.hidden = !message;
      this.errorMessage.textContent = message;
    }

    getDeliveryEstimate(row, quantity) {
      this.updateStockMessage(row, quantity);

      const stockContainer = row.querySelector('[data-stock-message]');
      const stockPrimary = stockContainer?.querySelector('[data-stock-primary]')?.textContent.trim();
      const stockSecondary = stockContainer?.querySelector('[data-stock-secondary]:not(.hidden)')?.textContent.trim();

      return [stockPrimary, stockSecondary].filter(Boolean).join(' ');
    }

    async submitRows(rows) {
      if (this.isSubmitting) return;

      const items = rows.map((row) => {
        const quantity = this.normalizeInput(row);
        const deliveryEstimate = this.getDeliveryEstimate(row, quantity);
        const item = {
          id: Number(row.dataset.variantId),
          quantity,
        };

        if (deliveryEstimate) {
          item.properties = {
            'Previsión de entrega': deliveryEstimate,
          };
        }

        return item;
      }).filter((item) => item.quantity > 0);

      if (items.length === 0) {
        this.handleErrorMessage('Selecciona al menos una cantidad válida.');
        this.updateButtonsState();
        return;
      }

      this.handleErrorMessage('');
      this.setLoadingState(true);

      const body = {
        items,
      };

      if (this.cart) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        this.cart.setActiveElement(document.activeElement);
      }

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const parsedResponse = await response.json();

        if (parsedResponse.status) {
          document.dispatchEvent(new CustomEvent('cart:error', {
            detail: {
              source: 'variant-table-form',
              productVariantId: items[0]?.id,
              errors: parsedResponse.description,
              message: parsedResponse.message,
            },
          }));
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'variant-table-form',
            productVariantId: items[0]?.id,
            errors: parsedResponse.description,
            message: parsedResponse.message,
          });
          this.handleErrorMessage(parsedResponse.description || parsedResponse.message || 'No se ha podido añadir a la cesta.');
          return;
        }

        items.forEach((item) => {
          document.dispatchEvent(new CustomEvent('variant:add', {
            detail: {
              variant: { id: item.id },
              quantity: item.quantity,
              sectionId: this.dataset.section,
            },
          }));
        });

        const cartResponse = await fetch(`${routes.cart_url}.js`);
        const cartState = await cartResponse.json();

        document.dispatchEvent(new CustomEvent('cart:change', {
          detail: {
            cart: cartState,
            sectionId: this.dataset.section,
          },
        }));

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'variant-table-form',
          productVariantId: items[0]?.id,
        });

        this.rows.forEach((row) => {
          const input = row.querySelector('.variant-table__quantity-input');
          if (input) input.value = row.dataset.initialQuantity || 1;
        });

        if (this.cart && parsedResponse.sections) {
          this.cart.renderContents(parsedResponse);
        } else if (!this.cart) {
          window.location = routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        this.handleErrorMessage('Ha ocurrido un error al añadir a la cesta.');
      } finally {
        this.setLoadingState(false);
      }
    }
  });
}
